import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useThemeHook from "../../hooks/useThemeHook";
import arthereumProvider, { db } from "../../../backend/ArthereumProvider";
import { imagesUrls, mainCryptoName } from "../../../constants";
import { UserModelData, PAGE_NAME } from "../../../types";
import FlatList from "../../components/FlatList";
import { useTranslationHook } from "../../hooks/useTranslationHook";
import { THEME_COLORS } from "../../themes/DarkLight";
import Text from "../../components/ThemedText";
import { useNavigate } from "react-router-dom";
import CommonButton from "../../components/CommonButton";
import CallToActionCard from "../../components/CallToActionCard";
import LoadingIndicator from "../../components/LoadingIndicator";
import { actions } from "../../redux";
import emitter, { EmitterEvents } from "../../utils/eventsEmitter";
import { doc, increment, updateDoc, setDoc, getDoc } from "firebase/firestore";

// Track Stake Pi button clicks for analytics
const trackStakeButtonClick = async () => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const analyticsRef = doc(db, "athAnalytics", `stake_clicks_${today}`);
    const docSnap = await getDoc(analyticsRef);
    
    if (docSnap.exists()) {
      await updateDoc(analyticsRef, {
        stake_pi_button_clicks: increment(1),
        lastUpdated: new Date()
      });
    } else {
      await setDoc(analyticsRef, {
        date: today,
        stake_pi_button_clicks: 1,
        lastUpdated: new Date()
      });
    }
  } catch (error) {
    console.log('Analytics tracking error:', error);
  }
};

const StakePage = () => {
  const userData: UserModelData = useSelector(
    (state) => (state as any)?.actualUser
  );
  const stakes = useSelector((state) => (state as any)?.stakes);

  const getTranslationText = useTranslationHook();

  const statsData = useMemo(
    () => [
      {
        title: getTranslationText("referal_boost"),
        value:
          userData?.miningInfos?.miningReferredUserCount +
          "/" +
          userData?.user?.referalsCount +
          " (" +
          userData?.miningInfos?.miningReferredUserCount * 0.25 +
          1 +
          ")",
        units: "",
        description: getTranslationText("referal_boost_desc"),
      },
      {
        title: getTranslationText("base_grant"),
        value: userData?.miningInfos?.baseGrant,
        units: " ATH",
        description: getTranslationText("base_grant_desc"),
      },
      {
        title: getTranslationText("staking_boost"),
        value: userData?.user?.stakeBoost,
        units: "",
        description: getTranslationText("staking_boost_desc"),
      },
    ],
    [userData]
  );

  const images = [
    imagesUrls["grant"],
    imagesUrls["stake"],
    imagesUrls["referral"],
  ];

  const theme = useThemeHook();

  const renderItem = ({ item, index }: { item: any; index: number }) => (
    <div className={index === 0 ? "pl-3" : "pl-0"}>
      <div className="w-64 bg-gray-50 border border-gray-200 rounded-2xl p-4 mr-3 flex flex-col gap-3">
        <img
          alt=""
          className="h-28 rounded-xl w-full object-cover"
          src={images[index]}
        />
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-900">
            {item.title}
          </span>
          <span className="text-sm font-semibold text-gray-900">
            {item.value}
          </span>
        </div>
        <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">
          {item.description}
        </p>
      </div>
    </div>
  );

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState(null);
  const ITEMS_PER_PAGE = 10;
  const dispatch = useDispatch();

  const getStakes = async (
    lastDoc: any | null,
    shouldRefresh: boolean = false
  ) => {
    if (stakes?.length !== 0 && !shouldRefresh) {
      return;
    }
    try {
      setLoading(true);
      const response = await arthereumProvider.getStakes(
        userData.user.id,
        lastDoc,
        ITEMS_PER_PAGE
      );
      if (shouldRefresh) {
        dispatch(actions.setStakes(response.stakes as any));
      } else {
        dispatch(actions.setStakes([...stakes, ...response.stakes] as any));
      }
      setHasMore(response.hasMore);
      setLastDoc(response.lastDoc as any);
    } catch (error) {
      console.error("Error fetching stakes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    emitter.on(EmitterEvents.UNSTAKE, () => handleRefresh());

    return () => {
      emitter.removeAllListeners(EmitterEvents.UNSTAKE);
    };
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await getStakes(null, true);
    setRefreshing(false);
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      getStakes(lastDoc, false);
    }
  };

  useEffect(() => {
    getStakes(null);
  }, []);

  const openStakeModal = (stake: any) => {
    emitter.emit(EmitterEvents.BOTTOM_SHEET_DISPLAY, {
      modal: { name: "stake", modalData: stake },
    });
  };

  const renderStakeItem = ({ item }: { item: any }) => (
    <div
      onClick={() => openStakeModal(item)}
      className="mx-3 my-1 p-4 bg-gray-50 border border-gray-200 rounded-2xl cursor-pointer hover:border-gray-300 transition-colors active:scale-[0.99]"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-900">
          {getTranslationText("stake_amount")}: {item.piAmountStaked} Pi
        </span>
        <span className="text-sm font-bold text-green-600">
          +{(item.stakeBoost * 100).toFixed(1)}%
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-600">
          {item?.unstaked
            ? `${getTranslationText("unstaked")}: ${new Date(
                item.unstakedAt?.seconds * 1000
              ).toLocaleDateString()}`
            : `${getTranslationText("ends_at")}: ${new Date(
                item.endsAt
              ).toLocaleDateString()}`}
        </span>
        <span
          className={`text-xs font-medium ${
            item.unstaked ? "text-red-600" : "text-green-600"
          }`}
        >
          {item.unstaked
            ? getTranslationText("unstaked")
            : getTranslationText("active")}
        </span>
      </div>
    </div>
  );

  const navigate = useNavigate();
  const goToStakePage = () => {
    trackStakeButtonClick(); // Track the button click
    navigate(PAGE_NAME.STAKE_SOME_PI);
  };

  return (
    <div className="min-h-screen bg-white">
      <FlatList
        pageKey="stake-page"
        itemsClassName="Stake"
        itemIdExtractor={({ item }: { item: any }) => item?.id}
        data={stakes}
        renderItems={renderStakeItem}
        listHeaderComponents={
          <div className="space-y-4 pb-3 pt-3">
            {/* Stats Cards Horizontal Scroll */}
            <div className="flex overflow-x-auto pb-2 scrollbar-hide">
              {statsData.map((stat, index) =>
                renderItem({ item: stat, index: index })
              )}
            </div>

            {/* Balance Cards Grid */}
            <div className="flex gap-3 px-3">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-2xl p-4">
                <p className="text-xs text-gray-600 font-medium mb-2">
                  {getTranslationText("active_miners", { mainCryptoName })}
                </p>
                <p className="text-2xl font-bold text-green-600">
                  {userData?.miningInfos?.miningReferredUserCount}/
                  {userData?.user?.referalsCount}
                </p>
              </div>

              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-2xl p-4">
                <p className="text-xs text-gray-600 font-medium mb-2">
                  {getTranslationText("next_grant")}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {userData?.miningInfos?.nextGrantValue.toFixed(2)} ATH
                </p>
              </div>
            </div>

            {/* Stake Button */}
            <div className="px-3">
              <CommonButton
                padding={"13px"}
                color={"white"}
                backgroundColor={THEME_COLORS[theme].piPurple}
                onClickHandle={goToStakePage}
                text={getTranslationText("stake_pi")}
              />
            </div>
          </div>
        }
        onRefresh={handleRefresh}
        onEndReached={loadMore}
        listFooterComponents={
          <>
            {!loading && !hasMore && stakes?.length === 0 && (
              <CallToActionCard
                buttonText={getTranslationText("stake_pi")}
                description={getTranslationText("stake_pi_description")}
                onClick={goToStakePage}
                title={getTranslationText("no_stakes")}
              />
            )}
            {loading && <LoadingIndicator />}
            <div className="h-24"></div>
          </>
        }
      />
    </div>
  );
};

export default StakePage;